<template>
  <DialogLayout v-model="isOpen" :title="title">
    <template v-if="!removingAllDevices" #activator="{ on }">
      <v-btn
        small
        color="warning"
        :disabled="renterSynced && !renterSyncedEndServiceEligible"
        v-on="on"
      >
        {{ action }}
      </v-btn>
    </template>

    <p class="subtitle-1">
      {{ questionText }}
    </p>

    <v-radio-group v-model="shouldRemoveDevices" dense>
      <v-radio
        :label="
          unitHasDevices
            ? 'Yes, I am removing the device(s)'
            : 'Yes, remove this Smart Unit from the renter'
        "
        :value="true"
      />
      <v-radio
        :label="`No, ${
          renterSyncedEndServiceEligible
            ? 'end the SD service for this renter (for all units), but leave device(s) in unit'
            : 'I am leaving the device(s) installed'
        }`"
        :value="false"
      />
    </v-radio-group>

    <v-textarea
      v-if="showReasonField"
      v-model="cause"
      label="Reason for ending service"
      placeholder="e.g. tenant said no, moving to a different unit, etc."
      rows="2"
      dense
      auto-grow
    />

    <template #actions>
      <v-btn color="primary" text @click="isOpen = false">Cancel</v-btn>
      <v-btn :disabled="!canContinue" color="primary" text @click="submit">
        Continue
      </v-btn>
    </template>
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import usePMS from "@components/common/usePMS";
import useBillingAccount from "./useBillingAccount";
import { ref, computed, watch } from "vue";

export default {
  name: "DialogRemoveDevices",
  components: { DialogLayout },
  props: {
    unit: {
      type: Object,
      default: null,
    },
    renterSynced: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: null,
    },
    unitCount: {
      type: Number,
      default: null,
    },
    endedService: {
      type: Boolean,
      default: null,
    },
  },
  setup(props, { emit }) {
    const shouldRemoveDevices = ref(null);
    const cause = ref("");
    const { isEndServiceEligible } = usePMS();
    const removingAllDevices = computed(() => !props.unit);
    const unitHasDevices = computed(
      () => Array.isArray(props.unit?.devices) && props.unit.devices.length > 0
    );

    const _value = ref(false);
    const isOpen = computed({
      get() {
        return props.value === null ? _value.value : props.value;
      },
      set(val) {
        if (props.value === null) {
          _value.value = val;
        } else {
          emit("input", val);
        }
      },
    });

    const { endedService: singletonEndedService } = useBillingAccount();
    const endedService = computed(() =>
      props.endedService !== null
        ? props.endedService
        : singletonEndedService.value
    );
    const renterSyncedEndServiceEligible = computed(
      () =>
        props.renterSynced && isEndServiceEligible.value && !endedService.value
    );
    const showReasonField = computed(
      () =>
        renterSyncedEndServiceEligible.value &&
        shouldRemoveDevices.value === false
    );
    const canContinue = computed(
      () =>
        shouldRemoveDevices.value !== null &&
        (!showReasonField.value || cause.value.trim() !== "")
    );
    const title = computed(() =>
      renterSyncedEndServiceEligible.value
        ? "End SD Service"
        : `Remove ${removingAllDevices.value ? "Renter" : "Smart Unit"}`
    );
    const action = computed(() =>
      renterSyncedEndServiceEligible.value ? "End Service" : "Remove Unit"
    );
    const questionText = computed(() =>
      removingAllDevices.value
        ? `Removing this renter will unassign all of their Smart Units. Are you also removing the device(s) from this renter's Smart Unit(s)?`
        : renterSyncedEndServiceEligible.value
        ? `You want to terminate StorageDefender service for this renter. Are you removing the device(s) and stopping service only for Smart Unit ${props.unit.name}?`
        : unitHasDevices.value
        ? `Are you also removing the device(s) from Smart Unit ${props.unit.name}?`
        : `Are you removing Smart Unit ${props.unit.name} from this renter?`
    );

    function resetState() {
      shouldRemoveDevices.value = null;
      cause.value = "";
    }

    function submit() {
      isOpen.value = false;
      emit("submit", {
        value: shouldRemoveDevices.value,
        selectedUnit: props.unit,
        cause: cause.value.trim(),
      });
    }

    watch(isOpen, (open) => {
      if (!open) resetState();
    });

    return {
      isOpen,
      shouldRemoveDevices,
      cause,
      showReasonField,
      canContinue,
      title,
      action,
      questionText,
      submit,
      removingAllDevices,
      renterSyncedEndServiceEligible,
      unitHasDevices,
    };
  },
};
</script>
